import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { AssistantDrawer } from '@/ui';
import { useAuth } from '@/auth/useAuth';
import { AssistantPanel } from '@/features/assistant/AssistantPanel';
import { AssistantScopeContext, type RecordScope } from '@/features/assistant/AssistantScope';
import { useAssistantAvailability } from '@/features/assistant/useAssistantAvailability';
import { useRecordScope } from '@/features/assistant/useRecordScope';
import { locatePath, pageName } from './navigation';
import { ASSISTANT_TOGGLE_ID, CommandStrip } from './CommandStrip';
import { NavRail } from './NavRail';
import { useRailExpanded } from './useRailExpanded';
import { ViewTabs } from './ViewTabs';

/**
 * The frame every signed-in screen renders inside.
 *
 * Three zones: a navigation rail on the inline-start edge, a command strip
 * above the work, and the working surface itself, with the assistant as a
 * fourth column that opens beside the content rather than on top of it. The
 * surface is full-bleed — pages decide their own density — because a shell that
 * imposes a centred, max-width column makes a dispatch board impossible and
 * every screen in this product either is a board or is a dense table.
 *
 * The assistant starts closed on every load and is not drawn at all when the workspace has it
 * switched off, so a screen never spends a column on something that is not there to use.
 *
 * The shell, not the page, draws the views of the current destination, as tabs
 * above the page on the destination's own routes. A page never builds its own
 * switcher, so every destination switches the same way.
 *
 * Two skip links, not one. "Skip to content" is the familiar one; "Skip to
 * navigation" matters more here, because the rail is long and a keyboard user
 * deep in a table needs a way back to it that is not thirty Shift+Tabs.
 *
 * @param children the page
 * @returns the shell
 */
export function AppShell({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { expanded: railExpanded, toggle: toggleRail } = useRailExpanded();
  const [assistantWanted, setAssistantWanted] = useState(false);
  const [selectedScope, setSelectedScope] = useState<RecordScope | null>(null);
  const { identity, user } = useAuth();
  const { pathname } = useLocation();
  const main = useRef<HTMLElement>(null);
  useEffect(() => {
    if (main.current) main.current.scrollTop = 0;
  }, [pathname]);
  const assistantAvailable = useAssistantAvailability();
  const assistantOpen = assistantWanted && assistantAvailable;
  const located = locatePath(pathname);
  const context = located === undefined ? undefined : pageName(located);
  const pageScope = useRecordScope(pathname, { enabled: assistantOpen });
  const scope = pageScope ?? (pathname === '/dispatch' ? selectedScope : null);

  // A single-page app changes the document without a page load, so the title
  // has to be maintained by hand — otherwise every browser tab, every bookmark
  // and every screen reader's page-change announcement says the same thing.
  useEffect(() => {
    const found = locatePath(pathname);
    document.title =
      found === undefined || found.destination.id === 'today'
        ? 'FieldOps CRM'
        : `${pageName(found)} · FieldOps CRM`;
  }, [pathname]);

  return (
    <div className="fo-shell" data-rail={railExpanded ? 'expanded' : 'collapsed'}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <a className="skip-link" href="#navigation">
        Skip to navigation
      </a>

      <NavRail expanded={railExpanded} onToggle={toggleRail} />

      <div className="fo-shell__column">
        <CommandStrip
          assistantOpen={assistantOpen}
          assistantAvailable={assistantAvailable}
          onToggleAssistant={() => {
            setAssistantWanted((wanted) => !wanted);
          }}
        />
        {/* tabIndex makes the skip link work: without it, focus would move to
            an element that cannot hold it and the next Tab would start from the
            top of the document again. */}
        <main ref={main} className="fo-shell__main" id="main" tabIndex={-1}>
          {user?.workspaceSlug === 'fieldops-sandbox' && (
            <div className="fo-sandbox-banner" role="status" data-testid="sandbox-banner">
              <strong>Sandbox workspace</strong>
              <span> — sample data for practice. Changes here do not affect live work.</span>
            </div>
          )}
          <ViewTabs />
          <AssistantScopeContext.Provider value={setSelectedScope}>
            {children}
          </AssistantScopeContext.Provider>
        </main>
      </div>

      <div className="fo-shell__assistant" id="assistant-drawer">
        <AssistantDrawer
          open={assistantOpen}
          onClose={() => {
            setAssistantWanted(false);
            // The drawer is only ever closed from inside it, so focus would be lost with it.
            document.getElementById(ASSISTANT_TOGGLE_ID)?.focus();
          }}
          {...(context === undefined ? {} : { context })}
        >
          {assistantOpen && (
            // Keyed on the principal only: remounting on every navigation threw
            // away a general chat mid-answer. The record assistant, which is about
            // the page, resets itself on recordKey.
            <AssistantPanel
              key={identity ?? 'signed-out'}
              recordKey={`${pathname}:${scope?.id ?? ''}`}
              scope={scope}
              defaultType={
                pathname.startsWith('/pipeline')
                  ? 'OPPORTUNITY'
                  : pathname.startsWith('/dispatch') || pathname.startsWith('/jobs')
                    ? 'APPOINTMENT'
                    : 'CUSTOMER'
              }
            />
          )}
        </AssistantDrawer>
      </div>
    </div>
  );
}
